import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Camera, Upload, Loader2, X, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Camper post-stay checkout report.
 * - REQUIRES a photo of the site state (proof of cleanliness).
 * - REQUIRES a comment for the host.
 * - REQUIRES a star rating (1-5) for the host/stay.
 * Submitted once per booking; visible to host afterwards. Rating is published
 * once both parties have rated (or rating window closes via background loop).
 */
export default function CheckoutReportDialog({ booking, open, onOpenChange, onSubmitted }) {
  const [photoUrl, setPhotoUrl] = useState("");
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  if (!booking) return null;

  const reset = () => { setPhotoUrl(""); setComment(""); setRating(0); setHoverRating(0); };

  const onClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(data.url);
      toast.success("Photo televersee");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (rating < 1 || rating > 5) { toast.error("Donnez une note de 1 à 5 étoiles"); return; }
    if (comment.trim().length < 5) { toast.error("Merci d'écrire un commentaire (min 5 caractères)"); return; }
    setSubmitting(true);
    try {
      // 1) Submit checkout report (photo + comment)
      await api.post(`/bookings/${booking.id}/checkout-report`, { photo_url: photoUrl, comment: comment.trim() });
      // 2) Submit rating for the host. Use the same comment so reviewers see one consistent message.
      try {
        await api.post("/ratings", { booking_id: booking.id, rating, comment: comment.trim() });
      } catch (err) {
        // If a rating already exists, ignore - report is the priority.
        const detail = err?.response?.data?.detail || "";
        if (!/deja évalué|déjà évalué|already/i.test(detail)) {
          console.error("Rating submit failed:", detail);
        }
      }
      toast.success("Merci ! Votre départ et votre avis ont été envoyés.");
      onSubmitted?.();
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const previewSrc = photoUrl?.startsWith("/api/") ? `${BACKEND_URL}${photoUrl}` : photoUrl;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl max-w-lg" data-testid="checkout-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Camera className="h-5 w-5 text-emerald-600" /> Confirmer votre départ</DialogTitle>
          <DialogDescription>
            Avant de quitter <span className="font-medium text-zinc-700">{booking.space_title}</span>, laissez un mot au propriétaire. Ajouter une photo de l'etat du terrain est recommande mais facultatif.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Photo */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Photo de l'etat du site <span className="text-zinc-400 normal-case tracking-normal text-[11px] font-normal">(optionnel mais recommande)</span></label>
            {photoUrl ? (
              <div className="relative rounded-xl overflow-hidden border border-zinc-200 bg-zinc-100">
                <img src={previewSrc} alt="Etat du terrain" className="w-full h-56 object-cover" data-testid="checkout-photo-preview" />
                <button
                  type="button"
                  onClick={() => setPhotoUrl("")}
                  className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"
                  data-testid="checkout-photo-remove"
                >
                  <X className="h-4 w-4 text-zinc-700" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full h-40 rounded-xl border-2 border-dashed border-zinc-300 hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors flex flex-col items-center justify-center gap-2 text-zinc-500"
                data-testid="checkout-photo-upload-btn"
              >
                {uploading ? (
                  <><Loader2 className="h-6 w-6 animate-spin" /><span className="text-xs">Upload en cours...</span></>
                ) : (
                  <><Upload className="h-6 w-6" /><span className="text-sm font-medium">Cliquer pour ajouter une photo</span><span className="text-xs">JPG / PNG, 5 Mo max</span></>
                )}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="checkout-photo-input" />
          </div>

          {/* Star rating */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Votre note du séjour <span className="text-red-500">*</span></label>
            <div className="flex items-center gap-1.5" data-testid="checkout-rating-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(n)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`rating-star-${n}`}
                  aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                >
                  <Star
                    className={`h-7 w-7 ${(hoverRating || rating) >= n ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`}
                  />
                </button>
              ))}
              {rating > 0 && (
                <span className="ml-2 text-sm font-medium text-zinc-700">{rating}/5</span>
              )}
            </div>
            <p className="text-[11px] text-zinc-400 mt-1">Cette note s&apos;affichera publiquement sur le profil de l&apos;hôte (à la fin de la fenêtre d&apos;évaluation).</p>
          </div>

          {/* Comment */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Mot au propriétaire <span className="text-red-500">*</span></label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Ex: Merci pour cet accueil! J'ai laisse le terrain propre, ramasse les dechets..."
              maxLength={1000}
              className="rounded-xl min-h-[100px]"
              data-testid="checkout-comment-input"
            />
            <p className="text-xs text-zinc-400 mt-1 text-right">{comment.length}/1000</p>
          </div>

          <div className="text-xs text-zinc-500 p-3 rounded-xl bg-amber-50 border border-amber-100">
            Cette information est visible par le propriétaire. Elle prouve l'état dans lequel vous quittez le terrain et facilite les futurs séjours.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => onClose(false)} data-testid="checkout-report-cancel">Plus tard</Button>
          <Button
            className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl"
            disabled={submitting || uploading || comment.trim().length < 5 || rating < 1}
            onClick={submit}
            data-testid="checkout-report-submit"
          >
            {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Envoi...</>) : "Confirmer mon départ et envoyer l'avis"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
